import styled from "styled-components";
import PropTypes from "prop-types";
import { theme } from "../theme";

export const Typography = styled(
  ({ component, variant = "body1", className, ...props }) => {
    const Element =
      component ||
      (["h1", "h2", "h3", "h4", "h5", "h6"].includes(variant) ? variant : "p");
    return <Element className={className} {...props} />;
  }
)(({
  variant = "body1",
  fontSize,
  fontWeight,
  strong,
  lineHeight = 1.5,
  m,
  mt,
  mb,
  ml,
  mr,
  my,
  mx,
  p,
  pt,
  pb,
  pl,
  pr,
  py,
  px,
  italic,
  align = "left",
  decoration = "none",
  ellipsis,
  wordBreak,
  display,
  gap,
  bgColor,
  color,
  colorVariant,
  alignItem,
  textCase,
  sx = {},
}) => {
  let size, weight, marginBottom;

  switch (variant) {
    case "h1":
      size = 60;
      weight = 800;
      marginBottom = 36;
      break;
    case "h2":
      size = 40;
      weight = 700;
      marginBottom = 30;
      break;
    case "h3":
      size = 32;
      weight = 600;
      marginBottom = 24;
      break;
    case "h4":
      size = 25;
      weight = 500;
      marginBottom = 20;
      break;
    case "h5":
      size = 20;
      weight = 500;
      marginBottom = 18;
      break;
    case "h6":
      size = 18;
      weight = 500;
      marginBottom = 15;
      break;
    case "body1":
      size = 16;
      weight = 400;
      marginBottom = 12;
      break;
    case "body2":
      size = 14;
      weight = 400;
      marginBottom = 10;
      break;
    case "body3":
        size = 12;
        weight = 400;
        marginBottom = 10;
        break;
    default:
      size = 16;
      weight = 400;
      marginBottom = 10;
  }

  // Determine values for breakpoints
  const resolveResponsiveValue = (value, defaultValue) =>
    typeof value === "object"
      ? {
          xs: value.xs || defaultValue,
          md: value.md || value.xs || defaultValue,
          lg: value.lg || value.md || value.xs || defaultValue,
          xl: value.xl || value.lg || value.md || value.xs || defaultValue,
        }
      : {
          xs: value || defaultValue,
          md: value || defaultValue,
          lg: value || defaultValue,
          xl: value || defaultValue,
        };

  const resolvedFontSize = resolveResponsiveValue(fontSize, size);
  const resolvedFontWeight = resolveResponsiveValue(fontWeight, weight);
  const resolvedLineHeight = resolveResponsiveValue(lineHeight, 1.5);
  const resolvedAlign = resolveResponsiveValue(align, "left");
  const resolvedMargin = resolveResponsiveValue(m, "0");
  const resolvedMarginBlock = resolveResponsiveValue(my, "0");
  const resolvedMarginBottom = resolveResponsiveValue(mb, marginBottom);
  const resolvedMarginTop = resolveResponsiveValue(mt);
  const resolvedMarginInline = resolveResponsiveValue(mx, "0");
  const resolvedMarginLeft = resolveResponsiveValue(ml, "0");
  const resolvedMarginRight = resolveResponsiveValue(mr, "0");
  const resolvedPadding = resolveResponsiveValue(p, "0");
  const resolvedPaddingBlock = resolveResponsiveValue(py, "0");
  const resolvedPaddingBottom = resolveResponsiveValue(pb, "0");
  const resolvedPaddingTop = resolveResponsiveValue(pt);
  const resolvedPaddingInline = resolveResponsiveValue(px, "0");
  const resolvedPaddingLeft = resolveResponsiveValue(pl, "0");
  const resolvedPaddingRight = resolveResponsiveValue(pr, "0");

  // Determine the color value
  const colorValue = color
    ? theme.colors[color]
      ? colorVariant
        ? theme.colors[color][colorVariant]
        : theme.colors[color].main
      : color
    : theme.colors.dark.main;
  const bgColorValue = bgColor
    ? theme.colors[bgColor]
      ? colorVariant
        ? theme.colors[bgColor][colorVariant]
        : theme.colors[bgColor].main
      : color
    : "transparent";

  // Determine the font family
  const fontFamily = variant.startsWith("h")
    ? theme.typography.headingFontFamily
    : theme.typography.fontFamily;

  return {
    display,
    gap,
    alignItems:alignItem,
    backgroundColor: bgColorValue,
    color: colorValue,
    fontFamily: fontFamily,
    fontSize: `${resolvedFontSize.xl / 16}rem`,
    fontWeight: strong ? "700" : resolvedFontWeight.xl,
    lineHeight: resolvedLineHeight.xl,
    verticalAlign: "middle",
    margin: `${resolvedMargin.xl / 16}rem`,
    marginBlock: my && `${resolvedMarginBlock.xl / 16}rem`,
    marginBlockStart: mt && `${resolvedMarginTop.xl / 16}rem`,
    marginBlockEnd: mb
      ? `${resolvedMarginBottom.xl / 16}rem`
      : my
        ? `${resolvedMarginBlock.xl / 16}rem`
        : m
          ? `${resolvedMargin.xl / 16}rem`
          : `${resolvedMarginBottom.xl / 16}rem`,
    marginInline: mx && `${resolvedMarginInline.xl / 16}rem`,
    marginInlineStart: ml && `${resolvedMarginLeft.xl / 16}rem`,
    marginInlineEnd: mr && `${resolvedMarginRight.xl / 16}rem`,
    padding: `${resolvedPadding.xl / 16}rem`,
    paddingBlock: py && `${resolvedPaddingBlock.xl / 16}rem`,
    paddingBlockStart: pt && `${resolvedPaddingTop.xl / 16}rem`,
    paddingBlockEnd: pb && `${resolvedPaddingBottom.xl / 16}rem`,
    paddingInline: px && `${resolvedPaddingInline.xl / 16}rem`,
    paddingInlineStart: pl && `${resolvedPaddingLeft.xl / 16}rem`,
    paddingInlineEnd: pr && `${resolvedPaddingRight.xl / 16}rem`,
    fontStyle: italic ? "italic" : "normal",
    textAlign: resolvedAlign.xl,
    textDecoration: decoration,
    whiteSpace: ellipsis ? "nowrap" : "normal",
    overflow: ellipsis ? "hidden" : "visible",
    textOverflow: ellipsis ? "ellipsis" : "clip",
    wordBreak:wordBreak?"break-all":"normal",
    textTransform:textCase,
    "& svg":{
      width:`${resolvedFontSize.xl / 16}rem`,
      height:`${resolvedFontSize.xl / 16}rem`,
    },
    // Responsive styles
    "@media (min-width: 1001px) and (max-width: 1200px)": {
      fontSize: `${resolvedFontSize.lg / 16}rem`,
      fontWeight: strong ? "600" : resolvedFontWeight.lg,
      lineHeight: resolvedLineHeight.lg,
      textAlign: resolvedAlign.lg,
      margin: `${resolvedMargin.lg / 16}rem`,
      marginBlock: my && `${resolvedMarginBlock.lg / 16}rem`,
      marginBlockStart: mt && `${resolvedMarginTop.lg / 16}rem`,
      marginBlockEnd: mb
        ? `${resolvedMarginBottom.lg / 16}rem`
        : my
          ? `${resolvedMarginBlock.lg / 16}rem`
          : m
            ? `${resolvedMargin.lg / 16}rem`
            : `${resolvedMarginBottom.lg / 16}rem`,
      marginInline: mx && `${resolvedMarginInline.lg / 16}rem`,
      marginInlineStart: ml && `${resolvedMarginLeft.lg / 16}rem`,
      marginInlineEnd: mr && `${resolvedMarginRight.lg / 16}rem`,

      padding: `${resolvedPadding.lg / 16}rem`,
      paddingBlock: py && `${resolvedPaddingBlock.lg / 16}rem`,
      paddingBlockStart: pt && `${resolvedPaddingTop.lg / 16}rem`,
      paddingBlockEnd: pb && `${resolvedPaddingBottom.lg / 16}rem`,
      paddingInline: px && `${resolvedPaddingInline.lg / 16}rem`,
      paddingInlineStart: pl && `${resolvedPaddingLeft.lg / 16}rem`,
      paddingInlineEnd: pr && `${resolvedPaddingRight.lg / 16}rem`,
      "& svg":{
        width:`${resolvedFontSize.lg / 16}rem`,
        height:`${resolvedFontSize.lg / 16}rem`,
      },
    },
    "@media (min-width: 601px) and (max-width: 1000px)": {
      fontSize: `${resolvedFontSize.md / 16}rem`,
      fontWeight: strong ? "600" : resolvedFontWeight.md,
      lineHeight: resolvedLineHeight.md,
      textAlign: resolvedAlign.md,
      margin: `${resolvedMargin.md / 16}rem`,
      marginBlock: my && `${resolvedMarginBlock.md / 16}rem`,
      marginBlockStart: mt && `${resolvedMarginTop.md / 16}rem`,
      marginBlockEnd: mb
        ? `${resolvedMarginBottom.md / 16}rem`
        : my
          ? `${resolvedMarginBlock.md / 16}rem`
          : m
            ? `${resolvedMargin.md / 16}rem`
            : `${resolvedMarginBottom.md / 16}rem`,
      marginInline: mx && `${resolvedMarginInline.md / 16}rem`,
      marginInlineStart: ml && `${resolvedMarginLeft.md / 16}rem`,
      marginInlineEnd: mr && `${resolvedMarginRight.md / 16}rem`,
      padding: `${resolvedPadding.md / 16}rem`,
      paddingBlock: py && `${resolvedPaddingBlock.md / 16}rem`,
      paddingBlockStart: pt && `${resolvedPaddingTop.md / 16}rem`,
      paddingBlockEnd: pb && `${resolvedPaddingBottom.md / 16}rem`,
      paddingInline: px && `${resolvedPaddingInline.md / 16}rem`,
      paddingInlineStart: pl && `${resolvedPaddingLeft.md / 16}rem`,
      paddingInlineEnd: pr && `${resolvedPaddingRight.md / 16}rem`,
      "& svg":{
        width:`${resolvedFontSize.md / 16}rem`,
        height:`${resolvedFontSize.md / 16}rem`,
      },
    },
    "@media (max-width: 600px)": {
      fontSize: `${resolvedFontSize.xs / 16}rem`,
      fontWeight: strong ? "500" : resolvedFontWeight.xs,
      lineHeight: resolvedLineHeight.xs,
      textAlign: resolvedAlign.xs,
      margin: `${resolvedMargin.xs / 16}rem`,
      marginBlock: my && `${resolvedMarginBlock.xs / 16}rem`,
      marginBlockStart: mt && `${resolvedMarginTop.xs / 16}rem`,
      marginBlockEnd: mb
        ? `${resolvedMarginBottom.xs / 16}rem`
        : my
          ? `${resolvedMarginBlock.xs / 16}rem`
          : m
            ? `${resolvedMargin.xs / 16}rem`
            : `${resolvedMarginBottom.xs / 16}rem`,
      marginInline: mx && `${resolvedMarginInline.xs / 16}rem`,
      marginInlineStart: ml && `${resolvedMarginLeft.xs / 16}rem`,
      marginInlineEnd: mr && `${resolvedMarginRight.xs / 16}rem`,
      padding: `${resolvedPadding.xs / 16}rem`,
      paddingBlock: py && `${resolvedPaddingBlock.xs / 16}rem`,
      paddingBlockStart: pt && `${resolvedPaddingTop.xs / 16}rem`,
      paddingBlockEnd: pb && `${resolvedPaddingBottom.xs / 16}rem`,
      paddingInline: px && `${resolvedPaddingInline.xs / 16}rem`,
      paddingInlineStart: pl && `${resolvedPaddingLeft.xs / 16}rem`,
      paddingInlineEnd: pr && `${resolvedPaddingRight.xs / 16}rem`,
      "& svg":{
        width:`${resolvedFontSize.xs / 16}rem`,
        height:`${resolvedFontSize.xs / 16}rem`,
      },
    },
    ...sx,
  };
});
Typography.propTypes = {
  component: PropTypes.elementType, // A custom element type for the component
  variant: PropTypes.oneOf([
    // Restrict variant to specific values
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "body1",
    "body2",
  ]),
  fontSize: PropTypes.oneOfType([
    // Font size can be a number or responsive object
    PropTypes.number,
    PropTypes.shape({
      xs: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
    }),
  ]),
  fontWeight: PropTypes.oneOfType([
    // Font weight can be a number or responsive object
    PropTypes.number,
    PropTypes.shape({
      xs: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
    }),
  ]),
  strong: PropTypes.bool, // Makes the font bold
  lineHeight: PropTypes.oneOfType([
    // Line height can be a number or responsive object
    PropTypes.number,
    PropTypes.shape({
      xs: PropTypes.number,
      md: PropTypes.number,
      lg: PropTypes.number,
      xl: PropTypes.number,
    }),
  ]),
  italic: PropTypes.bool, // Italicize text
  align: PropTypes.oneOf([
    // Text alignment options
    "left",
    "center",
    "right",
    "justify",
  ]),
  decoration: PropTypes.oneOf([
    // Text decoration options
    "none",
    "underline",
    "overline",
    "line-through",
  ]),
  ellipsis: PropTypes.bool, // Apply ellipsis when text overflows
  color: PropTypes.string, // Text color
  colorVariant: PropTypes.string, // Text color variant
  bgColor: PropTypes.string, // Background color
  m: PropTypes.number, // Margin
  mt: PropTypes.number, // Margin top
  mb: PropTypes.number, // Margin bottom
  ml: PropTypes.number, // Margin left
  mr: PropTypes.number, // Margin right
  my: PropTypes.number, // Margin top/bottom
  mx: PropTypes.number, // Margin left/right
  p: PropTypes.number, // Padding
  pt: PropTypes.number, // Padding top
  pb: PropTypes.number, // Padding bottom
  pl: PropTypes.number, // Padding left
  pr: PropTypes.number, // Padding right
  py: PropTypes.number, // Padding top/bottom
  px: PropTypes.number, // Padding left/right
  sx: PropTypes.object, // Custom styles
};

// Default props
Typography.defaultProps = {
  variant: "body1",
  align: "left",
  color: "dark",
  lineHeight: 1.5,
  decoration: "none",
  ellipsis: false,
  italic: false,
  strong: false,
  m: 0,
  p: 0,
};
